import React, { useEffect, useMemo, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';

import { RootState } from '@/src/reducers/types';

import Folders from './Folders/Folders';
import RootFolder from './Folders/RootFolder/RootFolder';

import { LibraryActionEnum } from '../../../api/getProjectPermissions';
import { useLibraryListStore } from '../../store/library-list.store';

function findPathToFolder(tree, targetId) {
  function dfs(node, path) {
    const newPath = [...path, node.id];
    if (node.id === targetId) return newPath;

    for (const child of node.children || []) {
      const result = dfs(child, newPath);
      if (result) return result;
    }

    return null;
  }

  for (const rootNode of tree) {
    const result = dfs(rootNode, []);
    if (result) return result;
  }

  return null; // Not found
}

const libraryMenu = { id: 'library', label: 'Library', icon: 'folder', class: '' };
const trashMenu = { id: 'trash', label: 'Trash', icon: 'delete_outline', class: 'trashBtn' };

const Sidebar = ({ folders, draggingData, draggingFromFiles }) => {
  const dispatch = useDispatch();
  const files = useSelector((state: RootState) => state.files);
  const permissions = useLibraryListStore((s) => s.permissions);
  const isEditor = useMemo(() => {
    return permissions.library?.actions?.some(
      (action) => action.name === LibraryActionEnum.MANAGE_ACCESS && action.allowed
    );
  }, [permissions]);
  const activeFolder = files.activeFolder;

  const [togglers, setTogglers] = useState([]);

  useEffect(() => {
    if (!activeFolder.referrer) return;

    handleToggles(false, findPathToFolder(folders, activeFolder.id));
  }, [folders, activeFolder]);

  const handleToggles = (bool, toggles) => {
    setTogglers((prev) => {
      const ids = Array.isArray(toggles) ? toggles : [toggles];

      if (bool) {
        // Collapse: remove all matching IDs
        return prev.filter((id) => !ids.includes(id));
      } else {
        // Expand: add all new IDs (without duplicates)
        const merged = new Set([...prev, ...ids]);
        return Array.from(merged);
      }
    });
  };

  const handleMenuClick = (menuItem) => {
    dispatch({
      type: 'UPDATE_FILES_SIDEBAR_ACTIVE',
      menu: { id: menuItem.id, label: menuItem.label },
    });
    dispatch({ type: 'UPDATE_FILES_ACTIVE_FOLDER', folder: 'null' });
  };

  const isLibraryActive =
    files.activeInSidebar.id === libraryMenu.id && files.activeFolder === 'null';
  const isTrashActive = files.activeInSidebar.id === trashMenu.id;

  return (
    <div className="filesSidebar">
      {/* Library */}
      <div className="menuItem">
        <div className="foldersListContainer">
          <RootFolder
            menuItem={libraryMenu}
            rowIndex={0}
            isDragDisabled={true}
            isDropDisabled={false}
            onHandleClick={permissions.library.access ? () => handleMenuClick(libraryMenu) : null}
            isActive={isLibraryActive}
            isEditor={isEditor}
          />
        </div>
        {permissions.library.access && (
          <div className="foldersListContainer">
            <Folders
              folders={folders || []}
              draggingData={draggingData}
              draggingFromFiles={draggingFromFiles}
              onToggler={handleToggles}
              togglers={togglers}
            />
          </div>
        )}
      </div>

      {/* Trash (Editor Only) */}
      {permissions.library.access && (
        <div className="menuItem">
          <a
            onClick={() => handleMenuClick(trashMenu)}
            className={`${trashMenu.class} ${isTrashActive ? 'active' : ''}`}
          >
            <div className="menu-content">
              <i className="material-icons">{trashMenu.icon}</i>
              <span>{trashMenu.label}</span>
            </div>
          </a>
        </div>
      )}
    </div>
  );
};

export default Sidebar;
